import { useHistory, useParams } from "react-router-dom";
import useGqlClient from "../../hooks/useGqlClient";
import {
  ActiveDealAccountQuery,
  DealGroupType,
  DealFirmType,
  DealQuery,
  DealRunner,
  FirmQuery,
  useActiveDealAccountQuery,
  useCreateDealAccountsMutation,
  useDealQuery,
  useFirmQuery,
  DealRoleType,
} from "../../graphql/generated";
import Loading from "../../components/Loading";
import { Steps } from "./CreateDeal";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "../../components/tailwind/Button";
import { Card } from "../../components/Card";
import { useEffect, useState } from "react";
import { Avatar } from "../../components/account/Avatar";
import { AnimatedModal } from "../../components/AnimatedModal";
import { CloseIcon } from "../../components/CloseIcon";
import { H4 } from "../../components/Heading";
import { useSelector } from "react-redux";
import { authSelectors } from "../../store/auth/selector";
import { useQueryClient } from "@tanstack/react-query";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import {
  DealRoleOption,
  DealRolesDropdown,
} from "@/src/components/permissions/DealRolesDropdown";

export function People() {
  const { dealId } = useParams<{ dealId: string }>();
  const history = useHistory();

  const client = useGqlClient();
  const dealQuery = useDealQuery(
    client,
    {
      id: dealId,
    },
    {
      enabled: dealId !== "",
    },
  );

  const [openModal, setOpenModal] = useState<
    "" | "firm" | "invite" | "member" | "add_people"
  >("");

  const [selectedDealFirm, setSelectedDealFirm] = useState<
    DealQuery["deal"]["sellers"][0]["dealFirms"][0] | null
  >(null);

  useEffect(() => {
    if (
      dealQuery.data &&
      dealQuery.data.deal.sellers.length > 0 &&
      dealQuery.data.deal.runner === DealRunner.Seller
    ) {
      const dealFirm = dealQuery.data.deal.sellers[0].dealFirms.find(
        (df) => df.type === DealFirmType.Original,
      );
      if (dealFirm) {
        setSelectedDealFirm(dealFirm);
      }

      return;
    }

    if (
      dealQuery.data &&
      dealQuery.data.deal.buyers.length > 0 &&
      dealQuery.data.deal.runner === DealRunner.Buyer
    ) {
      const dealFirm = dealQuery.data.deal.buyers[0].dealFirms.find(
        (df) => df.type === DealFirmType.Original,
      );
      if (dealFirm) {
        setSelectedDealFirm(dealFirm);
      }
    }
  }, [dealQuery.data, dealQuery.isRefetching]);

  if (dealQuery.error) {
    return (
      <div>
        <p>Something went wrong</p>
      </div>
    );
  }

  if (dealQuery.isPending || !dealQuery.data) {
    return <Loading />;
  }

  const dealFirmGroups =
    dealQuery.data.deal.runner === DealRunner.Seller
      ? dealQuery.data.deal.sellers
      : dealQuery.data.deal.buyers;

  return (
    <div className="flex-1">
      <div className="flex justify-center">
        <div className="hidden xl:block">
          <Steps currentStep={4} />
        </div>
        <div className="w-full xl:w-1/3">
          <p className="text-lg font-semibold">People</p>
          <p className="text-sm text-gray-500 font-light">
            Add more people from your firm. You can invite people from outside
            your firm later.
          </p>

          <div>
            {dealFirmGroups.map((fg) => {
              return (
                <div key={fg.id}>
                  <p className="font-semibold mt-4">{fg.name}</p>
                  <p className="text-sm text-gray-500 leading-tight">Group</p>
                  <Card margin="s 0 0 0">
                    <div className="p-3">
                      {selectedDealFirm ? (
                        <div>
                          <div className=" flex items-center justify-between">
                            <p className="font-semibold text-gray-600">
                              Members
                            </p>
                            <button
                              onClick={() => {
                                setOpenModal("add_people");
                              }}
                              className="text-indigo-500 font-semibold text-sm hover:text-indigo-600"
                            >
                              <div className="flex items-center">
                                <PlusIcon className="w-5 h-5 " />
                                <p>Add people from your firm</p>
                              </div>
                            </button>
                          </div>
                          {selectedDealFirm.dealAccounts.map((da) => {
                            return (
                              <div>
                                <div
                                  key={da.account.id}
                                  className="py-2 flex items-center justify-between"
                                >
                                  <div className="flex items-center flex-1">
                                    <Avatar account={da.account} size="m" />
                                    <div className="ml-3">
                                      <p className="text-sm font-semibold text-gray-600">
                                        {da.account.name}
                                      </p>
                                      <p className="text-xs leading-tight text-gray-500">
                                        {da.account.email}
                                      </p>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-400">
                                      {da.dealRole.name}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-8 gap-x-3">
            <Button
              onClick={() => {
                history.push(`/deal/${dealId}/dashboard`);
              }}
              variant="positive"
              text="Continue"
            />
          </div>
        </div>
      </div>
      {selectedDealFirm ? (
        // TODO: getting deleted anyway
        <AddPeopleFromFirm
          dealGroup={dealFirmGroups[0] as DealQuery["deal"]["sellers"][0]}
          dealId={dealId}
          open={openModal === "add_people"}
          firmGroupType={DealGroupType.Seller}
          onClose={() => {
            setOpenModal("");
          }}
        />
      ) : null}
    </div>
  );
}

function AddPeopleFromFirm(props: {
  open: boolean;
  onClose: () => void;
  dealGroup: DealQuery["deal"]["sellers"][0];
  firmGroupType: DealGroupType;
  dealId: string;
}) {
  const client = useGqlClient();
  const activeDealAccount = useActiveDealAccountQuery(client, {
    id: props.dealId,
  });

  if (activeDealAccount.error) {
    return null;
  }

  if (activeDealAccount.isPending || !activeDealAccount.data) {
    return null;
  }

  return (
    <AnimatedModal size="lg" open={props.open} onClose={props.onClose}>
      <PermissionCheck
        input={{
          permissionKey: "access:has_access",
          roleType: DealRoleType.Internal,
        }}
      >
        <div>
          <div className="flex items-center justify-between">
            <H4>Add people from your firm</H4>
            <CloseIcon onClose={props.onClose} />
          </div>
          <AddPeopleFromFirmContent
            dealGroup={props.dealGroup}
            onClose={props.onClose}
            activeDealAccount={activeDealAccount.data.deal.activeDealAccount}
            dealId={props.dealId}
            firmGroupType={props.firmGroupType}
          />
        </div>
      </PermissionCheck>
    </AnimatedModal>
  );
}

interface AccountToAdd {
  id: string;
  dealRoleId: string;
}

function AddPeopleFromFirmContent(props: {
  activeDealAccount: ActiveDealAccountQuery["deal"]["activeDealAccount"];
  dealGroup: DealQuery["deal"]["sellers"][0];
  firmGroupType: DealGroupType;
  onClose: () => void;
  dealId: string;
}) {
  const account = useSelector(authSelectors.account);
  const client = useGqlClient();
  const queryClient = useQueryClient();

  const [accountsToAdd, setAccountsToAdd] = useState<AccountToAdd[]>([]);

  const createDealAccounts = useCreateDealAccountsMutation(client);

  const firmQuery = useFirmQuery(client, {
    id: account && account.firm ? account.firm.id : "",
  });

  if (firmQuery.error) {
    return <div>Error</div>;
  }

  if (firmQuery.isPending || !firmQuery.data) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  const availableAccounts = firmQuery.data.firm.accounts.filter((acc) => {
    return !props.dealGroup.accounts.some(
      (dealAcc) => dealAcc.account.id === acc.id,
    );
  });

  return (
    <div>
      <div className="mt-3">
        {availableAccounts.length === 0 ? (
          <p className="text-gray-500">No accounts available to add</p>
        ) : null}
        {availableAccounts.map((acc) => {
          return (
            <div key={acc.id} className="py-2">
              <Account
                account={acc}
                dealId={props.dealId}
                onToggle={(dealRoleId, isChecked) => {
                  if (isChecked) {
                    setAccountsToAdd((prev) => {
                      return [
                        ...prev.filter((a) => a.id !== acc.id),
                        { id: acc.id, dealRoleId },
                      ];
                    });
                  } else {
                    setAccountsToAdd((prev) => {
                      return prev.filter((a) => a.id !== acc.id);
                    });
                  }
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex justify-end">
        <Button
          variant="positive"
          text="Add"
          isDisabled={
            accountsToAdd.length === 0 ||
            accountsToAdd.some((a) => a.dealRoleId === "")
          }
          isLoading={createDealAccounts.isPending}
          loadingText="Adding..."
          onClick={() => {
            createDealAccounts.mutate(
              {
                input: {
                  dealGroupId: props.dealGroup.id,
                  dealId: props.dealId,
                  accounts: accountsToAdd.map((a) => {
                    return {
                      accountId: a.id,
                      ndaSigned: true,
                      dealRoleId: a.dealRoleId,
                    };
                  }),
                },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: ["Deal", { id: props.dealId }],
                  });
                  props.onClose();
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}

function Account(props: {
  account: FirmQuery["firm"]["accounts"][0];
  dealId: string;
  onToggle(dealRoleId: string, isChecked: boolean): void;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [selectedRole, setSelectedRole] = useState<DealRoleOption | null>(null);

  return (
    <div>
      <div className="relative flex items-start">
        <div className="flex h-6 items-start">
          <input
            id={`account_to_add_checkbox_${props.account.id}`}
            aria-describedby="comments-description"
            name="account_to_add_checkbox"
            type="checkbox"
            checked={isChecked}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              setIsChecked(checked);
              props.onToggle(selectedRole?.id ?? "", checked);
            }}
          />
        </div>
        <div className="ml-3 text-sm leading-tight">
          <label
            htmlFor={`account_to_add_checkbox_${props.account.id}`}
            className="font-medium text-gray-900"
          >
            {props.account.name}
          </label>
          <p className="text-gray-500">{props.account.email}</p>
        </div>
      </div>
      {isChecked ? (
        <div className="ml-7 mt-3">
          <DealRolesDropdown
            dealId={props.dealId}
            roleType={DealRoleType.Internal}
            value={selectedRole?.id}
            onChange={(role) => {
              setSelectedRole(role);
              props.onToggle(role.id, true);
            }}
            size="s"
          />
        </div>
      ) : null}
    </div>
  );
}
